// src/components/JarvislySimpleComponents/SimpleLoadingModule.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React from 'react';
import { Grid, Spin } from 'antd';
import { printGlobalStateToConsole } from 'configs/ConfigIndex';
import translateX from 'utils/translateX';
import { useThemeStore } from 'stores/theme';
import { TEMPLATE } from 'constants/ThemeConstant';
import utils from 'utils/utils';
import useZustand from 'hooks/useZustand';
import {
  StyledContentCenter,
  StyledCover,
  StyledTitleCenter,
} from 'layouts/SharedComponents/StyledComponents';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { useBreakpoint } = Grid;
const DefaultIcon = null;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleLoadingModule = ({ visible, icon, title, translucid }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedLoadingApp = useZustand('boot', 'loadingApp', true, true);
  const storedLoadingModule = useZustand('boot', 'loadingModule', true, true);
  const storedNavCollapsed = useThemeStore(s => s.navCollapsed);
  const storedNavType = useThemeStore(s => s.navType);
  const storedDirection = useThemeStore(s => s.direction);
  const storedAppActivated = useZustand('app', 'appActivated');
  const currentTheme = useZustand('theme', 'currentTheme');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = screens.length === 0 ? false : !screens.includes('lg');

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const showComponent = typeof visible === 'boolean' ? visible : !!storedLoadingModule?.visible;
  const show = showComponent && !storedLoadingApp?.visible; // precedence
  const isTranslucid = translucid ?? !!storedLoadingModule?.translucid;
  // const isTranslucid =
  //   typeof translucid === 'boolean' ? translucid : !!storedLoadingPage?.translucid;

  const iconNode =
    icon === false
      ? null
      : (icon ??
        (storedLoadingModule?.icon === false ? null : (storedLoadingModule?.icon ?? DefaultIcon)));
  const finalTitle = title ?? storedLoadingModule?.title;
  const tip = finalTitle ? translateX(finalTitle) : null;

  // Calculate padding based on nav state and storedSubscription
  const getLayoutGutter = () => {
    if (['top', 'tile'].includes(storedNavType) || isMobile || !storedAppActivated) return 0;
    return storedNavCollapsed ? TEMPLATE.SIDE_NAV_COLLAPSED_WIDTH : TEMPLATE.SIDE_NAV_WIDTH;
  };

  const paddingLeft = getLayoutGutter();
  const direction = storedDirection || TEMPLATE.DIR_LTR;

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!showComponent) return null;

  return (
    <StyledCover
      translucid={isTranslucid}
      currentTheme={currentTheme}
      onDoubleClick={printGlobalStateToConsole}
      paddingLeft={paddingLeft}
      direction={direction}
      style={{
        zIndex: 1500,
        paddingLeft: direction === TEMPLATE.DIR_LTR ? paddingLeft : 0,
        paddingRight: direction === TEMPLATE.DIR_RTL ? paddingLeft : 0,
      }}
    >
      {show ? (
        <StyledContentCenter>
          {iconNode ? <Spin size="large" indicator={iconNode} /> : <Spin size="large" />}
          {tip ? <StyledTitleCenter currentTheme={currentTheme}>{translateX(tip)}</StyledTitleCenter> : null}
        </StyledContentCenter>
      ) : null}
    </StyledCover>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleLoadingModule;

// *******************************************************************************
// HELPERS
// *******************************************************************************
